import { useCallback } from "react";
import {
  type UseInstantTransformEvent,
  logNotFoundTrackingProperties,
  type EventTransformerHooks,
} from "../lib";
import type { CommonCoreRawEvent } from "./events";
import type { CommonCoreEvent } from "./events/amplitude";
import type { PageOpenedEvent } from "./events/amplitude/PageOpened";
import type { SearchSuggestionTappedEvent } from "./events/amplitude/SearchedSuggestionTapped";
import { TrackingEventName } from "./events/amplitude/TrackingEventName";
import type { ViewedWebviewEvent } from "./events/amplitude/ViewedWebviewEvent";
import type { PageOpenedRawEvent } from "./events/raw/PageOpened";
import type { SearchSuggestionTappedRawEvent } from "./events/raw/SearchSuggestionTapped";
import { TrackingRawEventName } from "./events/raw/TrackingRawEventName";
import type { ViewedWebviewRawEvent } from "./events/raw/WebviewRawEvent";

const useViewedWebviewTransformer: UseInstantTransformEvent<
  ViewedWebviewRawEvent,
  ViewedWebviewEvent
> = () => {
  return useCallback(
    (event) => ({
      name: TrackingEventName.viewed_webview,
      properties: {
        ...event.properties,
      },
    }),
    []
  );
};

const useSearchSuggestionTappedTransformer: UseInstantTransformEvent<
  SearchSuggestionTappedRawEvent,
  SearchSuggestionTappedEvent
> = () => {
  return useCallback(
    (event) => ({
      name: TrackingEventName.tapped_search_suggestion,
      properties: {
        ...event.properties,
      },
      encryptedProperties: event.encryptedProperties,
    }),
    []
  );
};

const getCurrentUrl = () => {
  if (typeof window !== "undefined" && window != null) {
    return `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  }
  logNotFoundTrackingProperties(
    "getCurrentUrl",
    "CORE_COMMON_HTS_EVENT_TRANSFORMER_HOOKS"
  );
  return "";
};

export const usePageOpenedTransformer: UseInstantTransformEvent<
  PageOpenedRawEvent,
  PageOpenedEvent
> = () => {
  return useCallback(
    (event) => ({
      name: TrackingEventName.launched_application,
      properties: {
        URL: getCurrentUrl(),
        landing_screen: event.properties.page,
      },
    }),
    []
  );
};

export const CORE_COMMON_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  CommonCoreRawEvent,
  CommonCoreEvent
> = {
  [TrackingRawEventName.WebviewViewed]: [useViewedWebviewTransformer],
  [TrackingRawEventName.SearchSuggestionTapped]: [
    useSearchSuggestionTappedTransformer,
  ],
  [TrackingRawEventName.PageOpened]: [usePageOpenedTransformer],
};
